import { Delete, Edit } from '@mui/icons-material'
import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TableCell,
	Typography
} from '@mui/material'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useHistory } from 'react-router'
import {
	CreateOrganizationPropertyModel,
	OrganizationPropertiesClient,
	OrganizationPropertyModel,
	OrganizationPropertyType
} from '../../../../api/clients/identity/OrganizationPropertiesClient'
import { FullscreenSpinner } from '../../../../components/feedback/circular'
import { TableColumn } from '../../../../components/tables/DTO'
import { FilteredTable } from '../../../../components/tables/FilteredTable'
import { FilterControl } from '../../../../components/tables/filters/DTO'
import { usePrompts } from '../../../../services/notifications/PromptService'
import { toastService } from '../../../../services/notifications/ToastService'
import AdministrationPageContainer from '../AdministrationPageContainer'
import { CreateOrganizationPropertyDialog } from './CreateOrganizationPropertyDialog'

export const OrganizationPropertiesPage = observer(() => {
	const organizationPropertiesClient = new OrganizationPropertiesClient()
	const promptService = usePrompts()
	const history = useHistory()

	const store = useLocalObservable(() => ({
		properties: undefined as OrganizationPropertyModel[] | undefined,
		createPropertyOpen: false,
	}))

	const handleCreatePropertyClicked = action(() => {
		store.createPropertyOpen = true
	})

	const handleCloseCreateProperty = action(() => {
		store.createPropertyOpen = false
	})

	const handleCreateProperty = async (
		property: CreateOrganizationPropertyModel,
	) => {
		try {
			runInAction(() => {
				store.createPropertyOpen = false
			})

			const { data } =
				await organizationPropertiesClient.AddOrganizationProperty(property)

			runInAction(() => {
				if (store.properties) store.properties.push(data)
			})

			toastService.displayToast({
				message: 'Organization property created',
				area: 'global',
				severity: 'success',
			})

			history.push(`/_administration/_organization-properties/${data.id}`)
		} catch (error) {
			alert(error)

			toastService.displayToast({
				message: 'Error creating organization property',
				area: 'global',
				severity: 'error',
			})
		}
	}

	useEffect(() => {
		organizationPropertiesClient.GetOrganizationProperties().then(
			action((v) => {
				store.properties = v.data
			}),
		)
	}, [])

	const onDeleteProperty = (property: OrganizationPropertyModel) =>
		organizationPropertiesClient
			.DeleteOrganizationProperty(property.id)
			.then(action(() => {
				const index = store.properties?.findIndex(
					(v) => v.id == property.id,
				)
				if (index === undefined) return
				store.properties = [...store.properties ?? []].splice(index, 1)
			}))

	const filterControls: FilterControl[] = useMemo(() => [
		{
			type: 'select',
			name: 'type',
			options: [{ label: "All Types", value: -1 }, { label: "Text", value: 0 }, { label: "List", value: 1 }],
			filterEntry: (entry, filterValue) =>
				filterValue === -1 ? true : filterValue === (entry as OrganizationPropertyModel).value.type
		},
		{
			type: 'search',
			name: 'search',
			column: "name"
		}
	], [])

	const columns: TableColumn<OrganizationPropertyModel>[] = useMemo(() => [
		{
			name: "name",
			label: "Name"
		},
		{
			name: "type",
			label: "Type",
			renderContent: (propertyType) => propertyType.value?.type === OrganizationPropertyType.String
				? 'Text'
				: 'List'
		},
		{
			renderHeading: <TableCell key={2} align="right">Actions</TableCell>,
			renderCell: (propertyType) =>
				<TableCell key={2} align="right">
					<IconButton
						size="large"
						onClick={() =>
							history.push(
								`/_administration/_organization-properties/${propertyType.id}`,
							)
						}
					>
						<Edit fontSize="small" />
					</IconButton>
					<IconButton
						size="large"
						onClick={() => {
							promptService
								.showDialog((props) => (
									<ConfirmDeletePropertyPrompt
										propertyName={propertyType.name}
										onCancel={() => props.close('cancel')}
										onConfirm={() => props.close('okay')}
									/>
								))
								.then((v) => {
									if (v == 'okay') onDeleteProperty(propertyType)
								})
						}}
					>
						<Delete fontSize="small" />
					</IconButton>
				</TableCell>
		}
	], [])

	return (
		<>
			<AdministrationPageContainer
				title="Organization Properties"
				actions={
					<div>
						<Button
							variant="contained"
							color="primary"
							startIcon={<Edit />}
							onClick={handleCreatePropertyClicked}
						>
							Create Property
						</Button>
					</div>
				}
			>
				{store.properties === undefined ? (
					<FullscreenSpinner />
				) : (
					<FilteredTable
						allEntries={store.properties}
						columns={columns}
						filterControls={filterControls}
					/>
				)}
			</AdministrationPageContainer>
			<CreateOrganizationPropertyDialog
				open={store.createPropertyOpen}
				existingPropertyNames={store.properties?.map((v) => v.name) ?? []}
				onCreateClicked={handleCreateProperty}
				onClose={handleCloseCreateProperty}
			/>
		</>
	)
})

type ConfirmDeletePropertyPromptProps = {
	propertyName: string
	onConfirm: () => void
	onCancel: () => void
}

const ConfirmDeletePropertyPrompt = (
	props: ConfirmDeletePropertyPromptProps,
) => {
	return (
		<>
			<DialogTitle>Confirm Delete Property</DialogTitle>
			<DialogContent>
				<Typography>
					Are you sure that you want to delete the <b>{props.propertyName}</b>{' '}
					organization property?
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button color="primary" onClick={props.onCancel}>
					Cancel
				</Button>
				<Button variant="contained" color="primary" onClick={props.onConfirm}>
					Confirm
				</Button>
			</DialogActions>
		</>
	)
}
